import {
  AADetectionResults,
  ConsentCheckResults,
  CountryCodeMode,
  GoogleTag,
  Prebid,
  SoftwallRecoveredType,
} from "./softwall/types";
import {
  BT_ENV,
  RL_PREVIEW,
  pageURLObject,
  topmostAccessibleWindow,
} from "./softwall/utils";
import { type CSSProperties } from "preact/compat";
import { detectLanguage } from "./softwall/utils/lngDetector";
import {
  detectFavicon,
  detectFont,
  detectBtnColor,
  getButtonStyle,
} from "./softwall/utils/autoDetection";

export type RenderMode = "always" | "api";

export type APIMode = "wait" | "auto";

export enum WallMode {
  soft = 0,
  hard = 1,
  adaptive = 2,
  dynamic = 3,
}

export type InstructionFields = {
  title: string;
  steps: string[];
};

export type i18nFields = {
  call_to_action?: string;
  landing_text?: string;
  landing_text_sca?: string;
  landing_text_hard_wall?: string;
  landing_text_hard_wall_sca?: string;
  name?: string;
  title?: string;
  continue?: string;
  custom_cta?: {
    primary_text?: string;
    secondary_text?: string;
  };
};

export type i18nResource = Record<string, i18nFields>;

export type Settings = {
  enabled: boolean;
  wall_mode: WallMode;
  logo: string;
  disable_automatic_detection?: boolean;
  view?: "modal" | "footer" | "custom-cta-modal";
  single_click_enabled: boolean;
  page_views_to_render?: string;
  premium_bypass_mode_enabled?: boolean;
  cta_button_color: string;
  font_type: string;
  allow_render_to_aa_users?: boolean;
  button_style: CSSProperties;
  render_interval_days?: string;
  attribution_window_days?: string;
  country_code_mode?: CountryCodeMode;
  country_code_list?: string[];
  script_loading_mode: "inject_from_tag_script" | "standalone";
  render_mode: RenderMode;
  api_mode: APIMode;
  custom_css?: string;
  custom_cta_modal?: {
    primary_cta_url: string;
    secondary_cta_url: string;
    primary_cta_text: string;
    secondary_cta_text: string;
  };
  dynamic_wall_settings?: {
    pageviews_count: number;
    dismiss_count: number;
  };
  allowlisted_urls?: string[];
  i18n?: i18nResource;
};

declare global {
  interface Window {
    __bt_tag_d?: {
      orgID?: string;
      version: string;
      siteInfo?: {
        websiteID?: string;
      };
    };
    __bt_intrnl: {
      traceID: string;
      aaDetectionResults: AADetectionResults;
      recoveryUserAttr: {
        recoveryType: SoftwallRecoveredType;
        mwRendered: boolean;
        attributionExpiry: number;
      };
      rlPVSent: boolean;
      country: string | undefined;
      timings: {
        shouldLog: boolean;
        navigationStartTimestamp: number;
        tagScriptLoadTimestamp: number;
      };
      consentData: ConsentCheckResults;
    };
    __bt_tag_am: {
      settings: Settings;
      kvpEventSent: boolean;
    };
    googletag: GoogleTag;
    _pbjsGlobals: PrebidKey[];

    [key: PrebidKey]: Prebid;

    // Function to load the recovery script
    __bt_recovery?: () => void;
    // Flag to indicate if the rlink script was already loaded in this page view
    __bt_rlink_already_invoked: boolean;
    // Flag to indicate if the rlink script was loaded from the tag script
    __bt_rlink_loaded_from_tag: boolean;
    // Mock config to run the standalone rlink script locally
    __bt_rlink_test?: {
      hostname?: string;
      params?: {
        o?: string;
        bt_env?: string;
      };
    };
  }

  // Add Brave as type to Navigator object, it's not included by default
  interface Navigator {
    brave: {
      isBrave(): boolean;
    };
  }
}

type PrebidKey = string;
export const apiDomain = getApiDomain();
export const assetsLocation = getAssetsLocation();
export let websiteID = window.__bt_tag_d?.siteInfo?.websiteID;
export const orgID = window.__bt_tag_d?.orgID || getOrgId();
export const bt_env = getBtEnv();

const hostname = getHostName();
export const traceID = window.__bt_intrnl?.traceID || getTraceID();
export const sessionID = getSessionId();

function getSessionId() {
  let sessionId = sessionStorage.getItem("BT_sid");
  if (!sessionId) {
    sessionId = randomRange(8, 10);
    sessionStorage.setItem("BT_sid", sessionId);
  }
  return sessionId;
}

function getSearchParamFromMetaUrl(search: string): string {
  const scriptURL = new URL(import.meta.url);
  const queryParams = new URLSearchParams(scriptURL.search);
  const param = queryParams.get(search);
  if (!param) throw new Error(`${search} query param not found`);

  return param;
}

function mockBtRLinkParams(param: string): string {
  if (
    ["development", "test"].includes(import.meta.env.MODE) &&
    window.__bt_rlink_test?.params?.[param]
  ) {
    return window.__bt_rlink_test.params[param];
  }
}

function getOrgId() {
  // script URL going to return initial source of injected script
  // on using redirect to new URL on dev testing pay attention that this value will not change to the redirect source
  const mockParam = mockBtRLinkParams("o");

  if (mockParam) {
    return mockParam;
  }

  const oParam = getSearchParamFromMetaUrl("o");
  // Cache websiteID in window.__bt_tag_d because the other chunks do not have access to the query param
  // @ts-expect-error: Initializing __bt_tag_d with empty object
  if (!window.__bt_tag_d) window.__bt_tag_d = {};
  window.__bt_tag_d.orgID = oParam;

  return oParam;
}

function getBtEnv(): string {
  const mockParam = mockBtRLinkParams("bt_env");

  if (mockParam) {
    return mockParam;
  }

  try {
    return getSearchParamFromMetaUrl("bt_env");
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
  } catch (e) {
    return BT_ENV;
  }
}

function getApiDomain() {
  const apiDomain = import.meta.env.VITE_API_DOMAIN;
  return verifyEnvSet("API_DOMAIN", apiDomain);
}

function getAssetsLocation() {
  const assetsLocation = import.meta.env.VITE_ASSETS_LOCATION;
  return verifyEnvSet("ASSETS_LOCATION", assetsLocation);
}

function verifyEnvSet(key: string, value: string | null | undefined): string {
  if (value === undefined || value === null || value === "") {
    throw new Error("missing env var for " + key);
  }
  return value;
}

function getHostName() {
  if (
    ["development", "test"].includes(import.meta.env.MODE) &&
    window.__bt_rlink_test?.hostname
  ) {
    return window.__bt_rlink_test?.hostname;
  }

  let hostname = pageURLObject.hostname;

  try {
    if (hostname === "disqusservice.com") {
      const searchParams = new URLSearchParams(
        topmostAccessibleWindow.location.search,
      );
      const sourceUrl = new URL(searchParams.get("sourceUrl"));
      hostname = sourceUrl.hostname + "-disqus";
    }
  } catch {
    hostname = topmostAccessibleWindow.location.hostname;
  }

  if (hostname.startsWith("www.")) {
    hostname = hostname.replace("www.", "");
  }

  return hostname;
}

function randomRange(min: number, max: number) {
  const chars =
    "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  if (
    typeof min !== "number" ||
    typeof max !== "number" ||
    min <= 0 ||
    max <= 0 ||
    min > max
  ) {
    throw new TypeError();
  }
  const n = Math.floor(Math.random() * (max + 1 - min)) + min;
  let s = "";
  for (let i = 0; i < n; i++) {
    s += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return s;
}

function getTraceID(): string {
  // since TraceID could be generated in bt_tag and mw injected to the page
  // we have to ensure that new value set in variable available for both scripts
  // @ts-expect-error: Initializing __bt_intrnl with empty object
  window.__bt_intrnl = window.__bt_intrnl || {};
  window.__bt_intrnl.traceID = randomRange(8, 10);

  return window.__bt_intrnl.traceID;
}

async function loadSetting() {
  if ((import.meta.env.DEV || RL_PREVIEW) && window.__bt_tag_am?.settings) {
    return window.__bt_tag_am.settings;
  }

  // @ts-expect-error: Initializing __bt_tag_am with empty object
  window.__bt_tag_am = window.__bt_tag_am || {};
  if (!orgID || !hostname) throw new Error("orgID or hostname not found");
  const websiteConfigUrl = new URL(`https://${apiDomain}/websiteconfig`);
  const lng = detectLanguage();

  websiteConfigUrl.searchParams.set("bt_env", bt_env);
  websiteConfigUrl.searchParams.set("o", orgID);
  websiteConfigUrl.searchParams.set("w", hostname);
  websiteConfigUrl.searchParams.set("l", lng);

  const response = await fetch(websiteConfigUrl.toString());

  if (!response.ok) {
    throw new Error(
      `Settings request failed. URL: ${websiteConfigUrl}; StatusCode: ${response.status}`,
    );
  }

  const settings = await response.json();
  if (!settings.messageWall) {
    throw new Error(`Invalid settings response: ${settings}`);
  }
  window.__bt_tag_am.settings = settings.messageWall;
  const w = settings.tagData?.w;

  // Cache websiteID in window.__bt_tag_d.siteInfo
  // @ts-expect-error: Initializing __bt_tag_d with empty object
  if (!window.__bt_tag_d) window.__bt_tag_d = {};
  if (!window.__bt_tag_d.siteInfo) window.__bt_tag_d.siteInfo = {};
  window.__bt_tag_d.siteInfo.websiteID = w;
  websiteID = w;

  return window.__bt_tag_am.settings;
}

export interface LocalSettings
  extends Omit<
    Settings,
    "page_views_to_render" | "render_interval_days" | "attribution_window_days"
  > {
  page_views_to_render: number;
  render_interval_days: number;
  attribution_window_days: number;
}

let settingsDeferred: Promise<Settings> | null = null;
const resolveSettings = async () => {
  if (settingsDeferred) {
    return settingsDeferred;
  }
  settingsDeferred = loadSetting();
  return settingsDeferred;
};

const getLogo = async (baseSettings: Settings) => {
  if (baseSettings.logo || baseSettings.disable_automatic_detection) {
    return baseSettings.logo;
  }
  return detectFavicon();
};

const getFont = (baseSettings: Settings) => {
  if (baseSettings.font_type) {
    return `${baseSettings.font_type}, sans-serif`;
  }

  if (baseSettings.disable_automatic_detection) {
    return `"Segoe UI", Helvetica, Arial, sans-serif;`;
  }

  return detectFont();
};

const getButtonColors = (baseSettings: Settings) => {
  if (baseSettings.cta_button_color) {
    return getButtonStyle(baseSettings.cta_button_color);
  }

  if (baseSettings.disable_automatic_detection) {
    return null;
  }

  return detectBtnColor();
};

const validatedDynamicWallSettings = (
  baseSettings: Settings,
): null | { pageviews_count: number; dismiss_count: number } => {
  if (baseSettings.wall_mode !== WallMode.dynamic) return null;

  // validate dynamic wall settings: if thresholds are not defined or set to zero,
  // reset them to the minimum allowed value to ensure proper functionality
  const dynamic_wall_settings = baseSettings.dynamic_wall_settings || {
    pageviews_count: 1,
    dismiss_count: 1,
  };
  if (baseSettings.dynamic_wall_settings?.pageviews_count === 0) {
    dynamic_wall_settings.pageviews_count = 1;
  }
  if (baseSettings.dynamic_wall_settings?.dismiss_count === 0) {
    dynamic_wall_settings.dismiss_count = 1;
  }

  return dynamic_wall_settings;
};

export const getBaseSettings = (): Promise<Settings> => {
  return resolveSettings();
};

export const buildLocalSettings = async (
  baseSettings: Settings,
): Promise<LocalSettings> => {
  const view = baseSettings.view || "modal";
  const page_views_to_render = Number(baseSettings.page_views_to_render);
  const render_interval_days = Number(baseSettings.render_interval_days);
  const attribution_window_days = Number(baseSettings.attribution_window_days);
  const allow_render_to_aa_users = Boolean(
    baseSettings.allow_render_to_aa_users,
  );
  const dynamic_wall_settings = validatedDynamicWallSettings(baseSettings);
  const logo = await getLogo(baseSettings);
  const font_type = getFont(baseSettings);
  const button_style = getButtonColors(baseSettings);

  return {
    ...baseSettings,
    logo,
    font_type,
    view,
    page_views_to_render,
    render_interval_days,
    attribution_window_days,
    allow_render_to_aa_users,
    button_style,
    dynamic_wall_settings,
    i18n: baseSettings.i18n,
  };
};
